/* Volatile — VolatileCart: single source of truth for cart contents.
 *
 * Why a shared module: the PDP cart-action ("Add to cart" / "Added ✓"),
 * the compact-sheet "+" → cart-icon swap, the "Visit cart (N)" label,
 * the header cart count, and the cart drawer all show information
 * derived from the same cart. Without one source of truth, each
 * surface has to re-fetch and re-derive — and they get out of sync.
 *
 * Design:
 *   • `VolatileCart.refresh()` fetches `/cart.js` and broadcasts.
 *   • Mutating endpoints (add.js, change.js) call `setState(cart)` with
 *     the response body so the shared state stays synchronised with
 *     the server WITHOUT a second round-trip.
 *   • Subscribers (`subscribe(fn)`) get the current state on attach
 *     and on every change, so a page that loads with items already in
 *     the cart paints the correct "Added ✓" state immediately.
 *   • Optimistic helpers (`optimisticAdd` / `optimisticChange`) update
 *     the UI before the network round-trip completes — and any state
 *     drift from the server gets reconciled by the next setState().
 *
 * Result: once a variant is removed in the cart drawer, every PDP
 * surface that depends on its membership flips back to its empty
 * state automatically — no class lingering, no stale labels.
 */
(function () {
  if (window.VolatileCart) return;

  var _state = { item_count: 0, items: [] };
  var _subs = new Set();

  function notify() {
    document.querySelectorAll('[data-header-cart-count]').forEach(function (el) {
      el.textContent = String(_state.item_count);
      el.hidden = _state.item_count === 0;
    });
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = '(' + _state.item_count + ')';
    });
    _subs.forEach(function (fn) {
      try { fn(_state); } catch (_) {}
    });
  }

  /* Server data structures vary slightly between /cart.js (full cart),
     /cart/add.js (added line), and /cart/change.js (full cart with
     sections). Normalise to `{ item_count, items: [{variant_id, qty}] }`. */
  function normalise(cart) {
    if (!cart) return { item_count: 0, items: [] };
    return {
      item_count: typeof cart.item_count === 'number' ? cart.item_count : 0,
      items: Array.isArray(cart.items)
        ? cart.items.map(function (it) {
            return {
              variant_id: Number(it.variant_id),
              quantity: Number(it.quantity) || 0,
              key: it.key,
              line: it.line,
            };
          })
        : [],
    };
  }

  function setState(cart) {
    _state = normalise(cart);
    notify();
  }

  function refresh() {
    return fetch('/cart.js', {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (cart) {
        if (cart) setState(cart);
        return _state;
      })
      .catch(function () { return _state; });
  }

  /* Optimistic mutations — call BEFORE the network request so the UI
     updates instantly. The next refresh() / setState() reconciles. */
  function optimisticAdd(variantId, qty) {
    qty = qty || 1;
    var id = Number(variantId);
    var existing = _state.items.find(function (it) { return it.variant_id === id; });
    if (existing) existing.quantity += qty;
    else _state.items.push({ variant_id: id, quantity: qty });
    _state.item_count += qty;
    notify();
  }
  function optimisticChange(variantId, newQty) {
    var id = Number(variantId);
    var item = _state.items.find(function (it) { return it.variant_id === id; });
    if (!item) return;
    var delta = newQty - item.quantity;
    item.quantity = newQty;
    _state.item_count = Math.max(0, _state.item_count + delta);
    if (newQty <= 0) {
      _state.items = _state.items.filter(function (it) { return it.variant_id !== id; });
    }
    notify();
  }

  function isVariantInCart(variantId) {
    var id = Number(variantId);
    return _state.items.some(function (it) { return it.variant_id === id && it.quantity > 0; });
  }
  function isAnyVariantInCart(variantIds) {
    if (!variantIds || !variantIds.length) return false;
    var idSet = new Set(variantIds.map(Number));
    return _state.items.some(function (it) { return idSet.has(it.variant_id) && it.quantity > 0; });
  }
  function getQty(variantId) {
    var id = Number(variantId);
    var item = _state.items.find(function (it) { return it.variant_id === id; });
    return item ? item.quantity : 0;
  }

  window.VolatileCart = {
    get state() { return _state; },
    refresh: refresh,
    setState: setState,
    optimisticAdd: optimisticAdd,
    optimisticChange: optimisticChange,
    isVariantInCart: isVariantInCart,
    isAnyVariantInCart: isAnyVariantInCart,
    getQty: getQty,
    subscribe: function (fn) {
      _subs.add(fn);
      // Fire once with the current state so subscribers don't have to
      // ask separately whether they should render the empty or
      // populated state on first paint.
      try { fn(_state); } catch (_) {}
      return function () { _subs.delete(fn); };
    },
  };

  window.dispatchEvent(new CustomEvent('volatile:cart:ready', { detail: { cart: window.VolatileCart } }));

  /* Hydrate from the server on every page load. The header section
     already renders the count synchronously from {{ cart.item_count }},
     but we still need the line-item list to know which variants are in
     the cart for the PDP per-SKU UI. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh);
  } else {
    refresh();
  }
})();
